import SearchIcon from '@mui/icons-material/Search';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { ApiService } from '../../services/api';
import { Store } from '../../types';
import { getUniqueChains } from '../../utils';
import { StoreTile } from './components/StoreTile';

const StoreList: React.FC = () => {
  const navigate = useNavigate();
  const { token, logout } = useAuthContext();
  const { toggleLoading, showErrorToast } = useNotificationContext();
  const [stores, setStores] = useState<Store[]>([]);
  const [filteredStores, setFilteredStores] = useState<Store[]>([]);
  const [storeSearch, setStoreSearch] = useState('');
  const [selectedChain, setSelectedChain] = useState('');

  const uniqueChains = getUniqueChains(stores);

  useEffect(() => {
    toggleLoading(true);
    ApiService.fetchStores(token)
      .then((storesData) => {
        setStores(storesData);
      })
      .catch((err) => {
        showErrorToast(err instanceof Error ? err.message : 'Error loading stores');
      })
      .finally(() => {
        toggleLoading(false);
      });
  }, [token, showErrorToast, toggleLoading]);

  useEffect(() => {
    let filtered = stores;
    if (storeSearch) {
      filtered = filtered.filter(
        (store) =>
          store.legibleName.toLowerCase().includes(storeSearch.toLowerCase()) ||
          store.storeName.toLowerCase().includes(storeSearch.toLowerCase())
      );
    }
    if (selectedChain) {
      filtered = filtered.filter((store) => store.chainName === selectedChain);
    }
    setFilteredStores(filtered);
  }, [storeSearch, selectedChain, stores]);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="w-full mx-auto">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold mb-6">Select Store</h1>
          <div className="flex items-center gap-4">
            <Link to="/forge" className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md">
              Forge
            </Link>
            <Link to="/embeddings" className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md">
              Embeddings
            </Link>
            <button
              onClick={() => logout()}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-md"
            >
              Sign Out
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search store</label>
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={storeSearch}
                  onChange={(e) => setStoreSearch(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Store name..."
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by chain
              </label>
              <select
                value={selectedChain}
                onChange={(e) => setSelectedChain(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All chains</option>
                {uniqueChains.map(({ label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredStores.map((store) => (
            <StoreTile
              key={store.storeId}
              store={store}
              onClick={() => navigate(`/stores/${store.storeName}`)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default StoreList;
